import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import { compose, filter, sort } from 'ramda';
import { isObject } from 'ramda-extension';
import { useDispatch } from 'react-redux';

import useFormData from '@gef-ui/form-core/hooks/state/useFormData';
import Autocomplete from '@gef-ui/form-material/Autocomplete';
import SubmitButton from '@gef-ui/form-material/SubmitButton';
import TextField from '@gef-ui/form-material/TextField';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { codelistProvider } from 'components';
import DatePickerFullWidth from 'components/atoms/GefMui/DatePickerFullWidth';
import { codelists } from 'constants/codelists';
import { MaxLengths } from 'constants/forms';
import {
	changeQuickFilter,
	fetchRAAnalysis,
	resetFormCriteria,
	setInitialFormValues,
} from 'features/views/roboAdvisory/actions';
import { FIELDS } from 'features/views/roboAdvisory/form';
import { sortAlphabeticallyBy } from 'utils';
import { getExactlyXYearsAgoAsDateObj } from 'utils/apiDateTime';

export const RAForm = ({ raAnalysisStates, raAnalysisTypes, raProductGroups }) => {
	const dispatch = useDispatch();
	const formData = useFormData();
	const formIsInitiated = isObject(formData);

	const raStatesPublicOnly = raAnalysisStates.filter((state) => state.isPublic);

	useEffect(() => {
		if (formIsInitiated) {
			dispatch(setInitialFormValues());
			dispatch(fetchRAAnalysis());
		}
	}, [dispatch, formIsInitiated]);

	const handleResetFormCriteria = useCallback(() => {
		dispatch(resetFormCriteria());
		dispatch(fetchRAAnalysis());
	}, [dispatch]);

	const handleSubmit = useCallback(() => {
		dispatch(changeQuickFilter(null));
		dispatch(fetchRAAnalysis());
	}, [dispatch]);

	const onlySpecifiedProducts = (product) => product?.isProduct === 1;

	const filteredAndSortedProductGroups = compose(
		sort(sortAlphabeticallyBy('shortDescription')),
		filter(onlySpecifiedProducts),
	)(raProductGroups);

	return (
		<Grid container>
			<Grid size={12}>
				<Grid container spacing={2}>
					<Grid size={3}>
						<TextField
							size="small"
							fieldName={FIELDS.surname}
							label={getLabelMessage('roboAdvisory:surname')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.SURNAME,
								},
							}}
						/>
					</Grid>
					<Grid size={3}>
						<TextField
							size="small"
							fieldName={FIELDS.firstName}
							label={getLabelMessage('roboAdvisory:firstName')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.NAME,
								},
							}}
						/>
					</Grid>
					<Grid size={3}>
						<TextField
							size="small"
							fieldName={FIELDS.businessName}
							label={getLabelMessage('roboAdvisory:businessName')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.BUSINESS_NAME,
								},
							}}
						/>
					</Grid>
					<Tooltip title={getLabelMessage('tooltips:birthNumber')}>
						<Grid size={3}>
							<TextField
								size="small"
								fieldName={FIELDS.personIn}
								label={getLabelMessage('roboAdvisory:personIn')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.BIRTH_NUMBER,
									},
								}}
							/>
						</Grid>
					</Tooltip>
					<Tooltip title={getLabelMessage('tooltips:taxNumber')}>
						<Grid size={3}>
							<TextField
								size="small"
								fieldName={FIELDS.businessIn}
								label={getLabelMessage('roboAdvisory:businessIn')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.TAX_NUMBER,
									},
								}}
							/>
						</Grid>
					</Tooltip>
					<Tooltip title={getLabelMessage('tooltips:postalCode')}>
						<Grid size={2}>
							<TextField
								size="small"
								fieldName={FIELDS.zipCode}
								label={getLabelMessage('roboAdvisory:zipCode')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.ZIP_CODE,
									},
								}}
							/>
						</Grid>
					</Tooltip>
					<Grid size={4}>
						<TextField
							size="small"
							fieldName={FIELDS.municipality}
							label={getLabelMessage('roboAdvisory:municipality')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.MUNICIPALITY,
								},
							}}
						/>
					</Grid>
					<Grid size={3}>
						<Autocomplete
							size="small"
							fieldName={FIELDS.product}
							label={getLabelMessage('roboAdvisory:product')}
							options={filteredAndSortedProductGroups}
							labelPropName="shortDescription"
						/>
					</Grid>
					<Grid size={3}>
						<Autocomplete
							multiple
							size="small"
							fieldName={FIELDS.types}
							label={getLabelMessage('roboAdvisory:types')}
							options={raAnalysisTypes}
							labelPropName="shortDescription"
						/>
					</Grid>
					<Grid size={3}>
						<Autocomplete
							multiple
							size="small"
							fieldName={FIELDS.states}
							label={getLabelMessage('roboAdvisory:states')}
							options={raStatesPublicOnly}
							labelPropName="description"
						/>
					</Grid>
					<Grid size={3}>
						<DatePickerFullWidth
							fieldName={FIELDS.createdDateFrom}
							label={getLabelMessage('roboAdvisory:createdDateFrom')}
							minDate={getExactlyXYearsAgoAsDateObj(-2)}
							disableFuture
						/>
					</Grid>
					<Grid size={3}>
						<DatePickerFullWidth
							fieldName={FIELDS.createdDateTo}
							label={getLabelMessage('roboAdvisory:createdDateTo')}
							minDate={getExactlyXYearsAgoAsDateObj(-2)}
							disableFuture
						/>
					</Grid>
					<Grid size={8} />
					<Grid size={2}>
						<Button variant="outlined" onClick={handleResetFormCriteria} fullWidth>
							{getLabelMessage('common:resetButton')}
						</Button>
					</Grid>
					<Grid size={2}>
						<SubmitButton
							variant="contained"
							onClick={handleSubmit}
							fullWidth
							text={getLabelMessage('common:searchButton')}
						/>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};

RAForm.propTypes = {
	raAnalysisStates: PropTypes.array,
	raAnalysisTypes: PropTypes.array,
	raProductGroups: PropTypes.array,
};

export default compose(
	codelistProvider(codelists.raAnalysisStates, 'raAnalysisStates'),
	codelistProvider(codelists.raAnalysisTypes, 'raAnalysisTypes'),
	codelistProvider(codelists.raProductGroups, 'raProductGroups'),
)(RAForm);
